import { createEnv } from '@t3-oss/env-core';
import { z } from 'zod';

/**
 * The single source of configuration. Every other module imports `env` from
 * here — nothing else should read `process.env` directly. Values are injected
 * at build time by Rspack's DefinePlugin (see rspack.config.ts) and validated
 * with Zod.
 *
 * Keys are unprefixed (`clientPrefix: ''`) because this app is bundled by
 * Rspack, not Vite — the whitelist in rspack.config.ts (CLIENT_ENV_KEYS)
 * controls what actually reaches the client bundle.
 */
export const env = createEnv({
  clientPrefix: '',
  // This is a pure static SPA — there is no server runtime. env-core's
  // client/server access guard is disabled by an empty `clientPrefix` (it then
  // treats every var as server-only and throws on browser access), so we mark
  // access as server-side to allow it everywhere. The real exposure boundary is
  // the CLIENT_ENV_KEYS whitelist in rspack.config.ts.
  isServer: true,
  client: {
    // Show the route in every build by default; this does not change the selected direct route
    // or enable the backend pool. Operators can explicitly hide it with false.
    CHINA_UPLOAD_ENABLED: z.enum(['true', 'false']).default('true'),
    // Deployment marker used to select the source-import authorization account.
    // Every hosting workflow sets it explicitly; there is no production fallback.
    APP_ENV: z.enum(['development', 'testing', 'production']),
    // Firebase Authentication (identity only). The project id must match the
    // backend sidecar's configured Firebase project — the token issuer and
    // audience are enforced server-side, so a mismatch rejects every request.
    FIREBASE_API_KEY: z.string().min(1),
    FIREBASE_AUTH_DOMAIN: z.string().min(1),
    FIREBASE_PROJECT_ID: z.string().min(1),
    FIREBASE_STORAGE_BUCKET: z.string().min(1),
    FIREBASE_MESSAGING_SENDER_ID: z.string().min(1),
    FIREBASE_APP_ID: z.string().min(1),
    FIREBASE_MEASUREMENT_ID: z.string().optional(),
    // Product analytics (PostHog). Both optional: when the key is absent the
    // analytics client is never initialized and every capture call is inert
    // (see src/shared/analytics/). Host defaults to PostHog US cloud in code.
    POSTHOG_KEY: z.string().optional(),
    POSTHOG_HOST: z.string().url().optional(),
  },
  runtimeEnv: {
    CHINA_UPLOAD_ENABLED: process.env.CHINA_UPLOAD_ENABLED,
    APP_ENV: process.env.APP_ENV,
    FIREBASE_API_KEY: process.env.FIREBASE_API_KEY,
    FIREBASE_AUTH_DOMAIN: process.env.FIREBASE_AUTH_DOMAIN,
    FIREBASE_PROJECT_ID: process.env.FIREBASE_PROJECT_ID,
    FIREBASE_STORAGE_BUCKET: process.env.FIREBASE_STORAGE_BUCKET,
    FIREBASE_MESSAGING_SENDER_ID: process.env.FIREBASE_MESSAGING_SENDER_ID,
    FIREBASE_APP_ID: process.env.FIREBASE_APP_ID,
    FIREBASE_MEASUREMENT_ID: process.env.FIREBASE_MEASUREMENT_ID,
    POSTHOG_KEY: process.env.POSTHOG_KEY,
    POSTHOG_HOST: process.env.POSTHOG_HOST,
  },
  emptyStringAsUndefined: true,
});
